import {
  createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore,
  type ComponentProps, type CSSProperties, type ReactNode,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { ConfirmDialog } from './confirm-dialog';
import { OverlayScope } from './overlay-scope';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { WorkspaceAction, WorkspaceActionOutlet, WorkspaceActionScope, WorkspaceActions, WorkspaceActionsProvider, useWorkspaceActionsStore, useWorkspaceDismissal } from './workspace-actions';
import './workspace-panel.css';

/*
 * THESIS: A task opens over its working page while navigation remains visible.
 * OWN-WORLD: Inherit the Unitfield capsule, neutral surfaces and shared controls.
 * STORY: Open a task, work across the available width, close to the same page.
 * FIRST VIEWPORT: A narrower recessed page behind a 26px-radius task surface;
 * title block at left, an optional centre slot (wizard step track), tonal
 * controls and a concentric circular close at right; the action island floats
 * at the bottom. A full-shell scrim dims the visible navigation.
 * FORM: A local extension of the existing shell, pinned by the Shopify reference.
 */

/*
 * A closing panel stays registered until its exit animation ends, so it keeps
 * its layer, but it no longer counts toward the shell's recess depth or the
 * interaction lock: the page starts restoring the moment the close begins.
 */
interface PanelEntry { id: string; depth: number; dismissible: boolean; modal: boolean; closing: boolean; onEscape: () => void }
type PanelState = Pick<PanelEntry, 'dismissible' | 'modal' | 'closing'>;
interface PanelStack {
  panels: readonly PanelEntry[];
  viewport: HTMLDivElement | null;
  setViewport: (node: HTMLDivElement | null) => void;
  register: (entry: PanelEntry) => () => void;
  update: (id: string, state: PanelState) => void;
}

/*
 * An open panel that unmounts (a loading placeholder giving way to the loaded
 * form, a re-keyed or remounted task) leaves its place to a panel that mounts
 * at the same depth in the same commit. That panel is already on screen: it
 * skips the entry and inherits where focus returns on close. Not per stack:
 * a standalone host is replaced together with its panel.
 */
interface PanelVacancy { id: string; depth: number; returnFocus: HTMLElement | null }
let vacancies: PanelVacancy[] = [];
function vacatePanel(vacancy: PanelVacancy) {
  vacancies.push(vacancy);
  // A replacement attaches later in the same synchronous commit, never after.
  queueMicrotask(() => { vacancies = vacancies.filter(entry => entry !== vacancy); });
}
function claimPanel(id: string, depth: number) {
  // StrictMode re-attaches the same panel; that is not a replacement.
  const index = vacancies.findIndex(entry => entry.depth === depth && entry.id !== id);
  return index < 0 ? null : vacancies.splice(index, 1)[0];
}

const PanelStackContext = createContext<PanelStack | null>(null);
const PanelDepthContext = createContext(0);

function usePanelStack() {
  const stack = useContext(PanelStackContext);
  if (!stack) throw new Error('WorkspacePanel requires a WorkspacePanelProvider and WorkspacePanelViewport');
  return stack;
}

/** One stack per shell; panels portal into the working capsule, beside navigation. */
export function WorkspacePanelProvider({ children }: { children: ReactNode }) {
  const [panels, setPanels] = useState<readonly PanelEntry[]>([]);
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const register = useCallback((entry: PanelEntry) => {
    setPanels(current => [...current, entry].sort((a, b) => a.depth - b.depth));
    return () => setPanels(current => current.filter(panel => panel.id !== entry.id));
  }, []);
  const update = useCallback((id: string, state: PanelState) => {
    setPanels(current => {
      const changed = (panel: PanelEntry) => panel.dismissible !== state.dismissible || panel.modal !== state.modal || panel.closing !== state.closing;
      if (!current.some(panel => panel.id === id && changed(panel))) return current;
      return current.map(panel => panel.id === id ? { ...panel, ...state } : panel);
    });
  }, []);
  const stack = useMemo(() => ({ panels, viewport, setViewport, register, update }), [panels, viewport, register, update]);
  return <WorkspaceActionsProvider><PanelStackContext value={stack}>{children}</PanelStackContext></WorkspaceActionsProvider>;
}

/** Task primitives work in a shell and on public/standalone screens. */
export function WorkspacePanelHost({ children }: { children: ReactNode }) {
  const parent = useContext(PanelStackContext);
  return parent ? children : <WorkspacePanelProvider><StandalonePanelHost>{children}</StandalonePanelHost></WorkspacePanelProvider>;
}
function disableStandaloneBackground(viewport: HTMLElement, modal: boolean) {
  const previous = new Map<HTMLElement, boolean>();
  let branch: HTMLElement = viewport;
  while (branch.parentElement) {
    // External providers own their body-level portals. The task's containing
    // page stays inert, while this explicitly non-modal path permits that UI.
    if (!modal && branch.parentElement === viewport.ownerDocument.body) break;
    for (const sibling of branch.parentElement.children) {
    if (sibling instanceof HTMLElement && sibling !== branch && !sibling.hasAttribute('data-radix-focus-guard')) {
      previous.set(sibling, sibling.inert); sibling.inert = true;
    }
    }
    if (branch.parentElement === viewport.ownerDocument.body) break;
    branch = branch.parentElement;
  }
  return () => previous.forEach((inert, node) => { node.inert = inert; });
}

function StandalonePanelHost({ children }: { children: ReactNode }) {
  const { panels, viewport } = usePanelStack();
  const open = panels.filter(panel => !panel.closing);
  const active = open.length > 0;
  const modal = open.at(-1)?.modal ?? true;
  // Standalone tasks have no AppShell to disable their surrounding page.
  useLayoutEffect(() => {
    if (!active || !viewport) return;
    return disableStandaloneBackground(viewport, modal);
  }, [active, viewport, modal]);
  return <div className="workspace-standalone-host"><div style={{ display: 'contents' }} inert={active}>{children}</div><WorkspacePanelViewport /></div>;
}

/**
 * Explicit page host for onboarding, public editors and embedded workspaces.
 * `pageActions="none"` leaves the island out: the page renders its own `WorkspaceActionOutlet` where it wants the bar
 * (the public application keeps it inside the form column, not floating over the viewport).
 */
export function WorkspaceSurface({ children, identity, className, pageActions = 'top' }: {
  children: ReactNode; identity?: string; className?: string; pageActions?: 'top' | 'none';
}) {
  const parent = useContext(PanelStackContext);
  const generated = useId();
  return parent ? children : <WorkspacePanelProvider><WorkspaceActionScope id={identity ?? generated} kind="page">
    <StandaloneSurface className={className} pageActions={pageActions}>{children}</StandaloneSurface>
  </WorkspaceActionScope></WorkspacePanelProvider>;
}
function StandaloneSurface({ children, className, pageActions }: { children: ReactNode; className?: string; pageActions: 'top' | 'none' }) {
  const count = useWorkspacePanelCount();
  return <div className={cn('workspace-surface-frame', className)} data-workspace-panel-active={count > 0}
    style={{ '--workspace-panel-depth': Math.min(count, 3) } as CSSProperties}>
    <div className="workspace-surface-page" inert={count > 0}>{children}</div>
    {count === 0 && pageActions === 'top' && <WorkspaceActionOutlet className="workspace-surface-actions" label="Page actions" />}
    <WorkspacePanelViewport />
  </div>;
}

interface Heading {
  title: ReactNode; description?: ReactNode; backAction?: ReactNode;
  /** Header centre slot, e.g. a wizard's step track. Hidden on narrow panels. */
  center?: ReactNode;
  /** Beside the title on the same row, e.g. a page switcher; it shrinks (truncates) before the title does. */
  accessory?: ReactNode;
  /** Right side, before close, e.g. a save state; replaces the panel's `headerControls` while registered. */
  controls?: ReactNode;
  titleRef?: ComponentProps<typeof DialogPrimitive.Title>['ref'];
  titleTabIndex?: number;
}
function createHeadingStore() {
  const entries = new Map<symbol, { heading: Heading; depth: number }>();
  const listeners = new Set<() => void>();
  let snapshot: Heading | null = null;
  const emit = () => {
    snapshot = [...entries.values()].sort((a, b) => b.depth - a.depth)[0]?.heading ?? null;
    listeners.forEach(listener => listener());
  };
  return {
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    snapshot: () => snapshot,
    register(token: symbol, heading: Heading, depth: number) { entries.set(token, { heading, depth }); emit(); return () => { entries.delete(token); emit(); }; },
  };
}
const HeadingContext = createContext<ReturnType<typeof createHeadingStore> | null>(null);
/** Compound and embedded screens publish metadata without another header. */
export function WorkspacePanelHeading({ title, description, backAction, center, accessory, controls, titleRef, titleTabIndex, depth = 0 }: Heading & { depth?: number }) {
  const store = useContext(HeadingContext);
  const [token] = useState(() => Symbol('workspace-panel-heading'));
  if (!store) throw new Error('WorkspacePanelHeading requires a WorkspacePanel');
  useLayoutEffect(() => store.register(token, { title, description, backAction, center, accessory, controls, titleRef, titleTabIndex }, depth), [store, token, title, description, backAction, center, accessory, controls, titleRef, titleTabIndex, depth]);
  return null;
}

export function useWorkspacePanelActive() {
  return useWorkspacePanelCount() > 0;
}

/** Open panels only: a closing panel already hands the page back. */
export function useWorkspacePanelCount() {
  return usePanelStack().panels.reduce((count, panel) => panel.closing ? count : count + 1, 0);
}

export function useWorkspacePanelNavigationBlocked() {
  return useWorkspacePanelActive();
}

const CloseRequestContext = createContext<(() => void) | null>(null);
/**
 * The panel's own close request, for in-body Cancel/Close commands: the same
 * rules as X (pending waits, unsaved edits ask "Discard changes?").
 */
export function useWorkspacePanelCloseRequest() {
  const request = useContext(CloseRequestContext);
  if (!request) throw new Error('useWorkspacePanelCloseRequest requires a WorkspacePanel');
  return request;
}

/** Place beside the shell surface, outside its stacking and inert subtrees. */
export function WorkspacePanelViewport() {
  const { setViewport } = usePanelStack();
  return <div ref={setViewport} className="workspace-panel-viewport" data-workspace-panel-viewport data-active={useWorkspacePanelActive()} />;
}

export interface WorkspacePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  /** Optional 32px tonal tile before the title. Without it there is no tile. */
  titleIcon?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Dynamic contents of the floating action island at the bottom of the panel. */
  headerActions?: ReactNode;
  /** Right-hand control groups, immediately before the circular close button. */
  headerControls?: ReactNode;
  /**
   * `false` locks X and Escape (provider flows, payments); the X stays
   * visibly disabled with `dismissReason` as its tooltip. Pending drafts lock
   * it on their own ("Saving…"); unsaved drafts ask before discarding.
   */
  dismissible?: boolean;
  /** Tooltip on the disabled X when `dismissible={false}`. */
  dismissReason?: string;
  /** Permit an external provider's body portal while keeping the page inert. */
  modal?: boolean;
  closeLabel?: string;
  className?: string;
  bodyClassName?: string;
  onOpenAutoFocus?: ComponentProps<typeof DialogPrimitive.Content>['onOpenAutoFocus'];
  onCloseAutoFocus?: ComponentProps<typeof DialogPrimitive.Content>['onCloseAutoFocus'];
}

/** Simple header glyphs on one vector grid, without half-pixel button offsets. */
export function WorkspacePanelIcon({ name, ...props }: Omit<ComponentProps<'svg'>, 'name'> & { name: 'folder' | 'undo' | 'redo' | 'download' | 'more' | 'next' | 'previous' | 'close' }) {
  const undo = <><path d="m8 3-5 5 5 5" /><path d="M3 8h10a6 6 0 0 1 0 12h-3" /></>;
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...props}>
    {name === 'folder' && <><path d="M21 9V7a2 2 0 0 0-2-2h-7l-2-2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7M2 9h16a3 3 0 0 1 3 3" /><path d="M16 14v5a3 3 0 0 0 6 0v-4a1.5 1.5 0 0 0-3 0v4" /></>}
    {name === 'undo' && undo}
    {name === 'redo' && <g transform="translate(24 0) scale(-1 1)">{undo}</g>}
    {name === 'download' && <path d="M12 3v13m-6-6 6 6 6-6M4 21h16" />}
    {name === 'more' && <g fill="currentColor" stroke="none"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></g>}
    {name === 'next' && <path d="m6 9 6 6 6-6" />}
    {name === 'previous' && <path d="m6 15 6-6 6 6" />}
    {name === 'close' && <path d="m6 6 12 12M6 18 18 6" />}
  </svg>;
}

/** Compact controls for the action island; the feature owns their behavior. */
export function WorkspacePanelAction({ appearance = 'secondary', className, ...props }: ComponentProps<typeof Button> & { appearance?: 'icon' | 'secondary' | 'primary' }) {
  return <WorkspaceAction {...props} className={className} appearance={appearance} />;
}

/** Adjacent header controls share one tonal pill. */
export function WorkspacePanelControlGroup({ className, ...props }: ComponentProps<'div'>) {
  return <div {...props} className={cn('workspace-panel-control-group', className)} />;
}

export function WorkspacePanelControl({ className, ...props }: ComponentProps<typeof Button>) {
  return <Button type="button" variant="ghost" size="iconSm" {...props} className={cn('workspace-panel-control', className)} />;
}

/**
 * The centre slot keeps its full labels only while it fits between equal side
 * columns (so it stays centred); otherwise the header marks it compact and the
 * slot collapses non-current labels. Width is measured, not guessed per label.
 */
function useCenterFit(enabled: boolean) {
  return useCallback((header: HTMLElement | null) => {
    if (!header || !enabled || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const width = (node: Element) => node.classList.contains('workspace-panel-title-stack')
      ? Math.max(0, ...[...node.children].map(line => line.scrollWidth)) : (node as HTMLElement).offsetWidth;
    const measure = () => {
      frame = 0;
      const center = header.querySelector<HTMLElement>(':scope > .workspace-panel-center');
      const heading = header.querySelector<HTMLElement>(':scope > .workspace-panel-heading');
      const controls = header.querySelector<HTMLElement>(':scope > .workspace-panel-controls');
      if (!center || !heading || !controls) return;
      // Read the full width synchronously; nothing paints between the writes.
      header.dataset.centerFit = 'full';
      const full = center.scrollWidth;
      const style = getComputedStyle(header);
      const inner = header.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
      const gap = parseFloat(style.columnGap) || 0;
      // Grid items stretch to their column; measure their content instead.
      const natural = (node: HTMLElement) => [...node.children].reduce((sum, child) => sum + width(child), 0)
        + Math.max(node.children.length - 1, 0) * (parseFloat(getComputedStyle(node).columnGap) || 0);
      const side = Math.max(Math.min(natural(heading), inner * .3), natural(controls));
      header.dataset.centerFit = full <= inner - 2 * (side + gap) ? 'full' : 'compact';
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    const resize = new ResizeObserver(schedule);
    resize.observe(header);
    // Labels and the current step change the full width.
    const mutations = new MutationObserver(schedule);
    mutations.observe(header, { childList: true, subtree: true, characterData: true });
    measure();
    return () => { resize.disconnect(); mutations.disconnect(); if (frame) cancelAnimationFrame(frame); delete header.dataset.centerFit; };
  }, [enabled]);
}

/**
 * A locked X is natively disabled and announced (`aria-disabled`). Its wrapper
 * takes hover/keyboard focus so the tooltip can still explain the lock.
 */
function PanelClose({ label, lock, onClose }: { label: string; lock: string | null; onClose: () => void }) {
  const control = <WorkspacePanelControl className="workspace-panel-close" aria-label={label} disabled={Boolean(lock)} aria-disabled={lock ? true : undefined}
    data-locked={lock ? 'true' : undefined} onClick={() => { if (!lock) onClose(); }}>
    <WorkspacePanelIcon name="close" />
  </WorkspacePanelControl>;
  if (!lock) return control;
  return <Tooltip><TooltipTrigger asChild><span className="workspace-panel-close-lock" tabIndex={0} role="group" aria-label={`${label}: ${lock}`}>{control}</span></TooltipTrigger><TooltipContent side="bottom">{lock}</TooltipContent></Tooltip>;
}

/** Matches the `@container (max-width: 700px)` island and header rules. */
const narrowPanelWidth = 700;

export function WorkspacePanel({
  open, onOpenChange, title, titleIcon, description, children, headerActions, headerControls,
  dismissible = true, dismissReason = 'Finish or cancel this task first', modal = true, closeLabel = 'Close', className, bodyClassName,
  onOpenAutoFocus, onCloseAutoFocus,
}: WorkspacePanelProps) {
  const { viewport, panels, register, update } = usePanelStack();
  const depth = useContext(PanelDepthContext);
  const id = useId();
  const store = useWorkspaceActionsStore();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const discardInFlight = useRef(false);
  const pending = useWorkspaceDismissal(id) === 'pending' || discarding;
  const canDismiss = dismissible && !pending;
  const closeLock = pending ? 'Saving…' : canDismiss ? null : dismissReason;
  // X and Escape share one request. Unsaved forms confirm; autosaved wizard
  // drafts and clean tasks close; pending or non-dismissible tasks wait.
  const requestClose = useCallback(() => {
    if (!open || !dismissible || discarding) return;
    const state = store.dismissal(id);
    if (state === 'pending') return;
    if (state === 'dirty') { setDiscardError(null); setConfirmDiscard(true); }
    else onOpenChange(false);
  }, [open, dismissible, discarding, store, id, onOpenChange]);
  const discardAndClose = async () => {
    if (!open || !dismissible || discardInFlight.current || store.dismissal(id) === 'pending') return;
    discardInFlight.current = true;
    setDiscardError(null);
    setDiscarding(true);
    try {
      const discarded = await store.discard(id);
      setConfirmDiscard(false);
      if (discarded) onOpenChange(false);
    } catch (cause) {
      // The task keeps its edits and the confirm says why. Owners with their
      // own notice (FormDrawer) return false instead and the confirm closes.
      setDiscardError(cause instanceof Error && cause.message ? cause.message : 'Changes could not be discarded. Try again.');
    } finally { discardInFlight.current = false; setDiscarding(false); }
  };
  if (!open && confirmDiscard) setConfirmDiscard(false);
  const [headingStore] = useState(createHeadingStore);
  const registeredHeading = useSyncExternalStore(headingStore.subscribe, headingStore.snapshot, () => null);
  const resolvedTitle = registeredHeading?.title ?? title ?? 'Workspace';
  const resolvedDescription = registeredHeading?.description ?? description;
  const center = registeredHeading?.center;
  const attachHeader = useCenterFit(Boolean(center));
  const descriptionId = useId();
  const surface = useRef<HTMLDivElement | null>(null);
  const escapeRequest = useRef<() => void>(() => {});
  useLayoutEffect(() => {
    escapeRequest.current = () => {
      if (!surface.current?.querySelector('.workspace-action-island[data-overflow-open="true"]')) requestClose();
    };
  }, [requestClose]);
  const returnFocus = useRef<HTMLElement | null>(null);
  const inheritedFocus = useRef<HTMLElement | null>(null);
  const [handoff, setHandoff] = useState(false);
  if (!open && handoff) setHandoff(false);
  const currentDismissible = useRef(canDismiss);
  const currentModal = useRef(modal);
  const currentClosing = useRef(!open);
  const attachPanel = useCallback((node: HTMLDivElement | null) => {
    surface.current = node;
    if (node) {
      const vacancy = currentClosing.current ? null : claimPanel(id, depth);
      if (vacancy) { inheritedFocus.current = vacancy.returnFocus; setHandoff(true); }
      const unregister = register({ id, depth, dismissible: currentDismissible.current, modal: currentModal.current, closing: currentClosing.current, onEscape: () => escapeRequest.current() });
      // The panel's own corners follow its narrow island, which a container
      // query cannot style on the container itself.
      const layout = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(([entry]) => {
        const narrow = entry.contentRect.width <= narrowPanelWidth;
        if ((node.dataset.layout === 'narrow') !== narrow) node.dataset.layout = narrow ? 'narrow' : 'wide';
      });
      layout?.observe(node);
      return () => {
        layout?.disconnect(); surface.current = null; unregister();
        if (!currentClosing.current) vacatePanel({ id, depth, returnFocus: returnFocus.current });
      };
    }
  }, [register, id, depth]);
  // The stack coordinates shell interaction across otherwise independent tasks.
  useLayoutEffect(() => {
    currentDismissible.current = canDismiss;
    currentModal.current = modal;
    currentClosing.current = !open;
    update(id, { dismissible: canDismiss, modal, closing: !open });
  }, [update, id, canDismiss, modal, open]);
  // Layers are counted among open panels; a closing panel keeps the layer it
  // had, so it exits above its parent while the parent widens back.
  const index = panels.findIndex(panel => panel.id === id);
  const closing = index >= 0 && panels[index].closing;
  const openPanels = panels.filter(panel => !panel.closing);
  const openIndex = openPanels.findIndex(panel => panel.id === id);
  const [lastLevel, setLastLevel] = useState(0);
  const level = closing ? lastLevel : Math.max(openIndex, 0);
  if (!closing && level !== lastLevel) setLastLevel(level);
  const active = closing || openIndex < 0 || openIndex === openPanels.length - 1;
  const style = {
    '--workspace-panel-level': level,
    '--workspace-panel-above': closing ? 0 : Math.min(3, Math.max(openPanels.length - level - 1, 0)),
    zIndex: level * 2 + 2,
  } as CSSProperties;

  // The shell owns background modality. Keeping Radix's root non-modal avoids
  // parent and child portals hiding each other when both mount initially open.
  return <PanelDepthContext value={depth + 1}>
    <DialogPrimitive.Root open={open} modal={false} onOpenChange={next => { if (next) onOpenChange(true); else requestClose(); }}>
      {viewport && <DialogPrimitive.Portal container={viewport}>
          {/* The scrim never dismisses: a stray click must not close a task. */}
          <div className="workspace-panel-backdrop" data-state={open ? 'open' : 'closed'} data-handoff={handoff || undefined} data-active={active && !closing} data-level={level} style={{ zIndex: level * 2 + 1 }} aria-hidden="true" />
          <DialogPrimitive.Content
            ref={attachPanel}
            data-slot="workspace-panel"
            data-active={active}
            data-closing={closing || undefined}
            data-handoff={handoff || undefined}
            aria-modal={active && !closing && modal || undefined}
            inert={!active || closing}
            aria-hidden={!active || closing || undefined}
            style={style}
            className={cn('workspace-panel', className)}
            aria-describedby={resolvedDescription ? descriptionId : undefined}
            onOpenAutoFocus={event => {
              const inherited = inheritedFocus.current;
              inheritedFocus.current = null;
              returnFocus.current = inherited?.isConnected ? inherited : viewport.ownerDocument.activeElement as HTMLElement | null;
              onOpenAutoFocus?.(event);
              if (!event.defaultPrevented) {
                event.preventDefault();
                (event.target as HTMLElement).focus({ preventScroll: true });
              }
            }}
            onCloseAutoFocus={event => {
              onCloseAutoFocus?.(event);
              if (event.defaultPrevented) return;
              event.preventDefault();
              const target = returnFocus.current;
              if (target?.isConnected && !target.closest('[inert]')) target.focus({ preventScroll: true });
            }}
            onEscapeKeyDown={event => {
              // Radix layers can mount parent-last when nested tasks start
              // open. Route task Escape through the visible stack in either
              // order; child popups still receive their own Escape first.
              event.preventDefault();
              openPanels.at(-1)?.onEscape();
            }}
            onInteractOutside={event => event.preventDefault()}
          >
            <HeadingContext value={headingStore}><CloseRequestContext value={requestClose}><WorkspaceActionScope id={id} kind="panel" enabled={open} active={active}>
            <OverlayScope id={id} active={active && open}>
              <header ref={attachHeader} className="workspace-panel-header" data-slot="workspace-panel-header" data-center={center ? 'true' : undefined}>
                <div className="workspace-panel-heading" data-slot="workspace-panel-heading">
                  {registeredHeading?.backAction && <span className="workspace-panel-back">{registeredHeading.backAction}</span>}
                  {titleIcon && <span className="workspace-panel-title-icon" aria-hidden="true">{titleIcon}</span>}
                  <div className="workspace-panel-title-stack">
                    <DialogPrimitive.Title ref={registeredHeading?.titleRef} tabIndex={registeredHeading?.titleTabIndex} className="workspace-panel-title">{resolvedTitle}</DialogPrimitive.Title>
                    {resolvedDescription && <DialogPrimitive.Description id={descriptionId} className="workspace-panel-description">{resolvedDescription}</DialogPrimitive.Description>}
                  </div>
                  {registeredHeading?.accessory && <div className="workspace-panel-accessory" data-slot="workspace-panel-accessory">{registeredHeading.accessory}</div>}
                </div>
                {center && <div className="workspace-panel-center" data-slot="workspace-panel-center">{center}</div>}
                {headerActions && <WorkspaceActions visible>{headerActions}</WorkspaceActions>}
                <div className="workspace-panel-controls">
                  {registeredHeading?.controls ?? headerControls}
                  <PanelClose label={closeLabel} lock={closeLock} onClose={requestClose} />
                </div>
              </header>
              <div className={cn('workspace-panel-body', bodyClassName)} data-slot="workspace-panel-body">{children}</div>
              <WorkspaceActionOutlet className="workspace-panel-action-outlet" label="Panel actions" placement="bottom" />
              <ConfirmDialog appearance="panel" tone="destructive" open={confirmDiscard} loading={pending} manualClose error={discardError}
                onOpenChange={next => { if (!pending) setConfirmDiscard(next); }}
                title="Discard changes?" description={<>Your edits to {resolvedTitle} will be lost.</>}
                cancelLabel="Keep editing" confirmLabel="Discard" onConfirm={discardAndClose} />
            </OverlayScope>
            </WorkspaceActionScope></CloseRequestContext></HeadingContext>
          </DialogPrimitive.Content>
      </DialogPrimitive.Portal>}
    </DialogPrimitive.Root>
  </PanelDepthContext>;
}
